// Shared travel expense schema + helpers used by both admin and client apps

export type TravelFieldType = 'date' | 'string' | 'currency' | 'number';

export interface TravelFieldDef {
  name: string;
  type: TravelFieldType;
  label: string;
  required?: boolean;
  default?: any;
  constraints?: {
    maxLength?: number;
    min?: number;
    max?: number;
    step?: number;
    pattern?: string;
  };
}

export interface TravelSchemaComputeTermField {
  kind: 'field';
  name: string;
}

export interface TravelSchemaComputeTermMileage {
  kind: 'mileage';
  distanceField: string;
  rateRef: 'medical_mileage';
}

export interface TravelSchema {
  version: number;
  fields: TravelFieldDef[];
  compute: {
    type: 'sum';
    terms: Array<TravelSchemaComputeTermField | TravelSchemaComputeTermMileage>;
    target: string; // usually 'amount'
  };
}

export const fetchTravelSchema = async (
  baseUrl: string,
  headers: HeadersInit
): Promise<TravelSchema> => {
  const url = `${baseUrl}/v2/fundrequests/schemas/travel`;
  const resp = await fetch(url, { method: 'GET', headers });
  if (!resp.ok) {
    throw new Error(`Failed to load travel schema: ${resp.status}`);
  }
  return (await resp.json()) as TravelSchema;
};

export const travelFieldNames = (schema: TravelSchema): string[] =>
  schema.fields.map((f) => f.name);

export const buildTravelDefault = (
  schema: TravelSchema,
  opts?: { withGL?: boolean }
): Record<string, any> => {
  const defaults: Record<string, any> = {};
  for (const f of schema.fields) {
    // If GL code should be omitted in client, skip unless withGL is true
    if (f.name === 'gl_code' && !opts?.withGL) continue;
    defaults[f.name] = f.default !== undefined ? f.default : null;
  }
  return defaults;
};

export interface MileageScheduleEntry {
  effective_date: string; // YYYY-MM-DD
  value: number;
  year?: string;
}

export interface AnnualRates {
  year?: number | string;
  medical_mileage?: number;
  mileage_schedule?: MileageScheduleEntry[];
  rates_by_year?: Record<string, any>;
  [key: string]: any;
}

/**
 * Pick the mileage rate in effect on a given travel date.
 *
 * The IRS can raise the rate mid-year, so a rate is identified by the date it
 * takes effect, not by its year. Returns undefined when no entry applies, so
 * callers can fall back to the year-keyed rates.
 */
export const resolveMileageRate = (
  annualRates: AnnualRates | null | undefined,
  travelDate: string | Date | null | undefined
): number | undefined => {
  const schedule = annualRates?.mileage_schedule;
  if (!Array.isArray(schedule) || schedule.length === 0) return undefined;

  const asDay = (d: string | Date): string =>
    typeof d === 'string' ? d.slice(0, 10) : d.toISOString().slice(0, 10);

  const day = travelDate ? asDay(travelDate) : asDay(new Date());
  if (!day) return undefined;

  // Entries are ascending by effective_date; take the last one that has started.
  let match: MileageScheduleEntry | undefined;
  for (const entry of schedule) {
    if (!entry?.effective_date) continue;
    if (entry.effective_date.slice(0, 10) <= day) match = entry;
    else break;
  }
  return match ? Number(match.value) : undefined;
};

/**
 * Overlay the date-resolved mileage rate onto a set of year-keyed rates, so
 * callers get one object whose medical_mileage is correct for the travel date.
 */
export const ratesForTravelDate = (
  annualRates: AnnualRates | null | undefined,
  travelDate: string | Date | null | undefined,
  yearRates?: AnnualRates | null
): AnnualRates | null | undefined => {
  const base = yearRates ?? annualRates;
  const rate = resolveMileageRate(annualRates, travelDate);
  if (rate === undefined) return base;
  return { ...(base || {}), medical_mileage: rate };
};

export const calculateTravelTotal = (
  itemValues: Record<string, any>,
  annualRates: AnnualRates | null | undefined,
  schema: TravelSchema
): number => {
  if (!schema?.compute || schema.compute.type !== 'sum') return 0;
  const mileageRate = Number(annualRates?.medical_mileage || 0) || 0;
  let total = 0;
  for (const term of schema.compute.terms) {
    if (term.kind === 'field') {
      const v = Number(itemValues?.[term.name] || 0);
      total += isNaN(v) ? 0 : v;
    } else if (term.kind === 'mileage') {
      const dist = Number(itemValues?.[term.distanceField] || 0);
      total += (isNaN(dist) ? 0 : dist) * mileageRate;
    }
  }
  return total;
};

